export type CustomFieldItem = { label: string; value: string };

export const CHARACTER_RESERVED_LABELS = [
  '类型',
  '性别',
  '种族',
  '视觉年龄',
  '简介',
  '画外音',
  'roleType',
  'gender',
  'race',
  'visualAge',
  'description',
  'isOffscreen',
  'personality',
] as const;

export const OUTFIT_RESERVED_LABELS = [
  '视觉描述',
  '服装',
  '发型',
  '妆容',
  '配饰',
  '性格',
  '音色',
  'visualDescription',
  'clothing',
  'hair',
  'makeup',
  'accessories',
  'personality',
  'voiceName',
] as const;

export const LOCATION_RESERVED_LABELS = [
  '内外景',
  '场景描述',
  '时间',
  '天气',
  '季节',
  '氛围',
  '简介',
  'location',
  'description',
  'timeOfDay',
  'weather',
  'season',
  'atmosphere',
] as const;

export const PROP_RESERVED_LABELS = [
  '类型',
  '用途',
  '简介',
  '使用者',
  'propType',
  'usage',
  'description',
  'ownerCharacter',
  'ownerLocation',
] as const;

export const STORYBOARD_RESERVED_LABELS = [
  '标题',
  '内容描述',
  '拍摄描述',
  '景别',
  '运镜',
  '时长',
  'title',
  'description',
  'shootingDescription',
  'shotSize',
  'cameraMovement',
] as const;

const MAX_FIELDS = 6;
const MAX_LABEL_LENGTH = 12;
const MAX_VALUE_LENGTH = 80;

export function codePointLength(value: string): number {
  return Array.from(value).length;
}

export function readEditableCustomFields(
  meta: Record<string, unknown> | null | undefined
): CustomFieldItem[] {
  if (!meta) return [];
  const raw = Object.prototype.hasOwnProperty.call(meta, 'customFields')
    ? meta.customFields
    : Object.prototype.hasOwnProperty.call(meta, 'custom_fields')
      ? meta.custom_fields
      : undefined;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label : '';
    const value = typeof record.value === 'string' ? record.value : '';
    return [{ label, value }];
  });
}

export function mergeElementMetaData(
  current: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!incoming) return { ...(current ?? {}) };
  const next: Record<string, unknown> = { ...(current ?? {}), ...incoming };
  if (!Object.prototype.hasOwnProperty.call(incoming, 'customFields')) {
    delete next.customFields;
  }
  if (!Object.prototype.hasOwnProperty.call(incoming, 'custom_fields')) {
    delete next.custom_fields;
  }
  return next;
}

export function readStoredCustomFields(
  meta: Record<string, unknown> | null | undefined
): CustomFieldItem[] | undefined {
  const hasFields =
    !!meta &&
    (Object.prototype.hasOwnProperty.call(meta, 'customFields') ||
      Object.prototype.hasOwnProperty.call(meta, 'custom_fields'));
  if (!hasFields || !meta) return undefined;
  const raw = meta.customFields ?? meta.custom_fields;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label.trim() : '';
    const value = typeof record.value === 'string' ? record.value.trim() : '';
    if (!label || !value) return [];
    return [{ label, value }];
  });
}

export function customFieldsForSubmit(
  fields: CustomFieldItem[] | undefined,
  reservedLabels: readonly string[]
): { fields: CustomFieldItem[] | undefined; error: string | null } {
  if (fields === undefined) return { fields: undefined, error: null };
  const completed = fields
    .map((field) => ({ label: field.label.trim(), value: field.value.trim() }))
    .filter((field) => field.label || field.value);
  return {
    fields: completed,
    error: validateCustomFields(completed, reservedLabels),
  };
}

export function validateCustomFields(
  fields: CustomFieldItem[],
  reservedLabels: readonly string[]
): string | null {
  if (fields.length > MAX_FIELDS) return `每个对象最多 ${MAX_FIELDS} 条补充信息`;
  return getCustomFieldIssues(fields, reservedLabels)[0]?.message ?? null;
}

export type CustomFieldIssue = {
  index: number;
  field: 'label' | 'value';
  kind: 'required' | 'invalid';
  message: string;
};

export function getCustomFieldIssues(
  fields: CustomFieldItem[],
  reservedLabels: readonly string[]
): CustomFieldIssue[] {
  const issues: CustomFieldIssue[] = [];
  const seen = new Set<string>();
  const reserved = new Set(reservedLabels);
  fields.forEach((field, index) => {
    const label = field.label.trim();
    const value = field.value.trim();
    if (!label || !value) {
      issues.push({
        index,
        field: !label ? 'label' : 'value',
        kind: 'required',
        message: '补充信息的名称和内容都不能为空',
      });
    }
    if (codePointLength(label) > MAX_LABEL_LENGTH) {
      issues.push({
        index,
        field: 'label',
        kind: 'invalid',
        message: `名称不能超过 ${MAX_LABEL_LENGTH} 个字`,
      });
    }
    if (codePointLength(value) > MAX_VALUE_LENGTH) {
      issues.push({
        index,
        field: 'value',
        kind: 'invalid',
        message: `内容不能超过 ${MAX_VALUE_LENGTH} 个字`,
      });
    }
    if (reserved.has(label)) {
      issues.push({
        index,
        field: 'label',
        kind: 'invalid',
        message: `「${label}」与已有字段同名`,
      });
    }
    if (label && seen.has(label)) {
      issues.push({
        index,
        field: 'label',
        kind: 'invalid',
        message: `「${label}」重复，同名只保留一条`,
      });
    }
    if (label) seen.add(label);
  });
  return issues;
}
